import API from "@core/api/api";

export const fetchProducts = async (params) => {
  const response = await API.get(`/products?${params}`);
  return response.data;
};

export const fetchProductByID = async (productId) => {
  const response = await API.get(`/products/${productId}`);
  return response.data;
};

export const fetchPackagesForProduct = async (productId) => {
  const response = await API.get(`/products/${productId}/packages`);
  return response.data;
};

export const fetchPickupsForProduct = async (productId) => {
  const response = await API.get(`/products/${productId}/pickups`);
  return response.data;
};

export const createProduct = async (product) => {
  const response = await API.post(`/products`, product);
  return response.data;
};

export const updateProduct = async ({ id, values }) => {
  const response = await API.put(`/products/${id}`, values);
  return response.data;
};

export const updateProductStock = async ({
  id,
  stock,
  reason,
  notes,
  adjustmentType,
}) => {
  const response = await API.put(`/products/${id}/stock`, {
    stock,
    reason,
    notes,
    adjustmentType,
  });
  return response.data;
};

export const getProductStockHistory = async (productId, params = {}) => {
  // Filter out empty string values to avoid validation errors
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== "" && value != null)
  );
  const queryString = new URLSearchParams(filteredParams).toString();
  const url = `/products/${productId}/stock/history${
    queryString ? `?${queryString}` : ""
  }`;
  const response = await API.get(url);
  return response.data;
};

export const bulkUpdateProductStock = async (stockUpdates, reason) => {
  const response = await API.put("/products/stock/bulk", {
    stockUpdates,
    reason,
  });
  return response.data;
};

export const getLowStockProducts = async (threshold = 10) => {
  const response = await API.get(`/products/stock/low?threshold=${threshold}`);
  return response.data;
};

export const getStockSummary = async () => {
  const response = await API.get("/products/stock/summary");
  return response.data;
};

// New stock calculation APIs
export const calculateProductStock = async (productId, options = {}) => {
  const queryString = new URLSearchParams(options).toString();
  const url = `/products/${productId}/stock/calculate${
    queryString ? `?${queryString}` : ""
  }`;
  const response = await API.get(url);
  return response.data;
};

export const calculateAllProductsStock = async (options = {}) => {
  const queryString = new URLSearchParams(options).toString();
  const url = `/products/stock/calculate-all${
    queryString ? `?${queryString}` : ""
  }`;
  const response = await API.get(url);
  return response.data;
};

export const getStockMovementAnalytics = async (productId, options = {}) => {
  const queryString = new URLSearchParams(options).toString();
  const url = `/products/${productId}/stock/analytics${
    queryString ? `?${queryString}` : ""
  }`;
  const response = await API.get(url);
  return response.data;
};

// Enhanced stock history creation
export const createStockHistoryRecord = async (data) => {
  const response = await API.post("/products/stock/history", data);
  return response.data;
};

export const bulkCreateStockHistory = async (movements) => {
  const response = await API.post("/products/stock/history/bulk", {
    movements,
  });
  return response.data;
};

// Stock validation
export const validateProductStockMovements = async (productId) => {
  const response = await API.post(`/products/${productId}/stock/validate`);
  return response.data;
};

// Validate specific stock movement
export const validateStockMovement = async (movementId) => {
  const response = await API.put(`/products/stock/history/${movementId}/validate`);
  return response.data;
};

// Total stock from history
export const getTotalStockFromHistory = async (productId, options = {}) => {
  const queryString = new URLSearchParams(options).toString();
  const url = `/products/${productId}/stock/history/total${
    queryString ? `?${queryString}` : ""
  }`;
  const response = await API.get(url);
  return response.data;
};

export const getBulkTotalStockFromHistory = async (productIds, options = {}) => {
  const response = await API.post("/products/stock/history/total/bulk", {
    productIds,
    ...options,
  });
  return response.data;
};

export const getStockMovementTimeline = async (productId, options = {}) => {
  const queryString = new URLSearchParams(options).toString();
  const url = `/products/${productId}/stock/history/timeline${
    queryString ? `?${queryString}` : ""
  }`;
  const response = await API.get(url);
  return response.data;
};

// Get stock analytics for a product
export const getStockAnalytics = async (productId, params = {}) => {
  try {
    const queryParams = new URLSearchParams(params);
    const response = await API.get(
      `/products/${productId}/stock/analytics?${queryParams}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching stock analytics:", error);
    throw error;
  }
};

// Get recent stock movements
export const getRecentStockMovements = async (limit = 10, params = {}) => {
  try {
    const queryParams = new URLSearchParams({ limit, ...params });
    const response = await API.get(`/products/stock/recent?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching recent stock movements:", error);
    throw error;
  }
};

// Get low stock products with movement data
export const getLowStockProductsWithMovements = async (threshold = 10) => {
  try {
    const response = await API.get(
      `/products/stock/low?threshold=${threshold}&includeMovements=true`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching low stock products with movements:", error);
    throw error;
  }
};
